import React from 'react';
import { useIntl } from 'react-intl';
import { ModalBody, ModalFooter } from 'reactstrap';
import { TypedMemo } from '../../../common/types';
import { LoadingView } from '../../../common/views/LoadingView';
import { ErrorView } from '../../../common/views/ErrorView/ErrorView';
import { SMLButton } from '../../components/primitives/SMLButton';
import { formatClock } from './addedAssetFacts';
import { addedAssetsMessages as messages } from './addedAssetsMessages';
import { AddAssetRequest, closeAddAssetDialog } from './addAssetDialogStore';
import { useYouTubeAdd } from './useYouTubeAdd';
import { YouTubeCandidateRow } from './YouTubeCandidateRow';
import { YouTubeLinkField } from './YouTubeLinkField';
import styles from './YouTubeTab.module.scss';

interface YouTubeTabProps {
  request: AddAssetRequest;
  tabs: React.ReactNode;
}

/**
 * "From YouTube" (design a2, a3): the videos of the connected channel, newest first, with the best
 * match picked; "Paste a link" for any other video. The title and the picture of a pasted link come
 * from YouTube on the server (oEmbed); no channel login is needed.
 */
const YouTubeTabComponent: React.FC<YouTubeTabProps> = ({ request, tabs }) => {
  const intl = useIntl();
  const tab = useYouTubeAdd(request, closeAddAssetDialog);
  const songLength = formatClock(request.songDurationSec);

  let content: React.ReactNode;
  if (tab.isLoading) {
    content = <LoadingView />;
  } else if (tab.loadError !== undefined) {
    content = <ErrorView error={tab.loadError} retry={() => tab.retry().catch(console.error)} />;
  } else if (tab.mode === 'list') {
    content = (
      <>
        <div className={styles.channelRow}>
          <span>
            {intl.formatMessage(messages.videosOn, { channel: <b key="channel">{tab.channel?.title ?? 'YouTube'}</b> })}
          </span>
          <span className={styles.grow} />
          <button type="button" className={styles.link} onClick={tab.showPaste}>
            {intl.formatMessage(messages.pasteLink)}
          </button>
        </div>
        <div className={styles.list} role="radiogroup" aria-label={intl.formatMessage(messages.tabYouTube)}>
          {tab.candidates.map((candidate) => (
            <YouTubeCandidateRow
              key={candidate.videoId}
              candidate={candidate}
              catalogItemUuid={request.catalogItemUuid}
              isPicked={tab.picked === candidate.videoId}
              onPick={tab.pick}
            />
          ))}
        </div>
        {tab.error !== null && (
          <span className={styles.error} role="alert">
            {tab.error}
          </span>
        )}
      </>
    );
  } else {
    content = (
      <>
        {tab.hasList && (
          <div className={styles.channelRow}>
            <span className={styles.grow} />
            <button type="button" className={styles.link} onClick={tab.showList}>
              {intl.formatMessage(messages.pickFromChannel)}
            </button>
          </div>
        )}
        <YouTubeLinkField value={tab.link} state={tab.linkState} error={tab.error} onChange={tab.changeLink} />
      </>
    );
  }

  const showMatchHint = tab.mode === 'list' && tab.pickedCandidate?.bestMatch === true && songLength !== null;

  return (
    <>
      <ModalBody className={styles.body}>
        {tabs}
        {content}
      </ModalBody>
      <ModalFooter>
        {showMatchHint && (
          <span className={styles.hint}>{intl.formatMessage(messages.bestMatchHint, { length: songLength })}</span>
        )}
        <SMLButton onClick={closeAddAssetDialog}>{intl.formatMessage(messages.cancel)}</SMLButton>
        <SMLButton variant="primary" disabled={!tab.canSubmit || tab.isAdding} onClick={tab.submit}>
          {intl.formatMessage(tab.isAdding ? messages.linking : messages.useVideo)}
        </SMLButton>
      </ModalFooter>
    </>
  );
};

export const YouTubeTab = TypedMemo(YouTubeTabComponent);
